export const CONNECTIVITY_USER_MESSAGE =
  'Unable to connect. Check your signal and try again.'

export const UPLOAD_CONNECTIVITY_USER_MESSAGE =
  'Upload problem encountered. Check your signal and try again later.'

const GATEWAY_STATUSES = new Set([502, 503, 504])
const TIMEOUT_STATUSES = new Set([408])

// Best-effort browser / fetch signals when no HTTP Response exists.
// These are NOT an ANAPI contract — see docs/specs/abr-anapi-connectivity-errors/SPEC.md
const FETCH_FAILURE_PATTERNS = [
  /failed to fetch/i,
  /load failed/i,
  /networkerror when attempting to fetch/i,
  /network request failed/i,
  /internet connection appears to be offline/i,
  /networkerror/i,
]

export class NetworkConnectivityError extends Error {
  constructor({
    cause = null,
    reason = 'unreachable',
    httpStatus = null,
    context = 'api',
  } = {}) {
    super(
      context === 'upload'
        ? UPLOAD_CONNECTIVITY_USER_MESSAGE
        : CONNECTIVITY_USER_MESSAGE
    )
    this.name = 'NetworkConnectivityError'
    this.reason = reason
    this.httpStatus = httpStatus
    this.context = context
    this.cause = cause
    this.isConnectivityError = true
  }
}

export const isConnectivityError = (error) =>
  Boolean(error && (error instanceof NetworkConnectivityError || error.isConnectivityError))

const looksLikeFetchFailure = (err) => {
  if (!err) return false
  if (err.name === 'AbortError') return true

  const message = typeof err.message === 'string' ? err.message : String(err)
  if (FETCH_FAILURE_PATTERNS.some((re) => re.test(message))) {
    return true
  }

  // Chromium sometimes surfaces TypeError with a fetch-related message only.
  if (err.name === 'TypeError' && /fetch/i.test(message)) {
    return true
  }

  return false
}

/**
 * Classify a rejected fetch / XHR before any HTTP Response exists.
 * Returns NetworkConnectivityError or null (caller keeps original).
 */
export function classifyFetchFailure(err, { online = typeof navigator !== 'undefined' ? navigator.onLine : true, context = 'api' } = {}) {
  if (isConnectivityError(err)) {
    return err
  }

  if (!looksLikeFetchFailure(err)) {
    return null
  }

  const reason = online === false ? 'offline' : (err?.name === 'AbortError' ? 'timeout' : 'unreachable')
  return new NetworkConnectivityError({ cause: err, reason, context })
}

/**
 * Classify an HTTP Response status that indicates gateway / connectivity trouble.
 * Returns NetworkConnectivityError or null (caller keeps ErrorResponse / HTTP Error).
 */
export function classifyHttpFailure(status, { context = 'api' } = {}) {
  const code = Number(status)
  if (GATEWAY_STATUSES.has(code)) {
    return new NetworkConnectivityError({ reason: 'gateway', httpStatus: code, context })
  }
  if (TIMEOUT_STATUSES.has(code)) {
    return new NetworkConnectivityError({ reason: 'timeout', httpStatus: code, context })
  }
  return null
}
